import { useEffect, useState } from 'react';
import { PolyfillFeatures } from './settings-helper';
import { IAppSettings, SettingsCache } from '../consts/settings-cache';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';

import { useTenant } from '@parcel2go/npm-p2g-app';
import { P2GImageMessage } from '~/components/imagemessage';
import { TenantSettingKeys } from '../consts/tenant-setting-keys';
import useSmartSendVersionFeature from '~/apps/profile/use-smartsend-version-feature';
import useOnLoad from '~/routes/use-on-load';
import { LocalStorageKeys } from '~/apps/smartsend/lib/consts/LocalStorageKeys';

interface props {
  children: React.ReactNode;
}

const SettingsWrapper: React.FC<props> = (props) => {
  const { tenant, settings } = useTenant();
  const { getSmartSendSettings } = useSmartSendApi();
  const { getPreferredSmartSendVersion } = useSmartSendVersionFeature();
  const [loading, setLoading] = useState<'LOADING' | 'ERROR' | 'LOADED'>('LOADING');

  // Load the preferred SmartSend version on component load, without holding the app for it. Until
  // it arrives the default version applies; getPreferredSmartSendVersion stores the answer, and
  // everything that reads the version reads that store when it needs it. Waiting here held every
  // signed-in page on "Loading..." for GET /user-setting, the my-account edge's most throttled
  // route (B7), on any browser without a stored version.
  useOnLoad(() => {
    if (!localStorage.getItem(LocalStorageKeys.SmartSendPreferredVersion)) {
      getPreferredSmartSendVersion();
    }
  });

  const onSettingsLoaded = (x: IAppSettings) => {
    PolyfillFeatures(x, tenant);

    SettingsCache.currencyCode = settings[TenantSettingKeys.CurrencyCode] ?? 'GBP';
    SettingsCache.profile = tenant;
    SettingsCache.settings = { ...SettingsCache.settings, ...x };

    // Tool Name
    if (!SettingsCache.settings.SmartSend.Name) {
      SettingsCache.settings.SmartSend.Name = 'Smart Send';
    }

    if (!SettingsCache.settings.SmartSend.SmartSendProviderName) {
      SettingsCache.settings.SmartSend.SmartSendProviderName = 'Parcel2Go';
    }
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const begin = async () => {
      try {
        setLoading('LOADING');
        const settings = await getSmartSendSettings();
        if (settings && settings.success && settings.result) {
          onSettingsLoaded(settings.result);
          setLoading('LOADED');
        } else {
          throw new Error('Failed to load settings');
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setLoading('ERROR');
        timeoutId = setTimeout(begin, 5000);
      }
    };

    begin();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  if (loading === 'LOADING') {
    return (
      <P2GImageMessage
        image="/theme/smartsend/notifications/loading.png"
        message="Loading..."
        contentKey="image-myaccount-loading"
        showLoader
      />
    );
  }

  if (loading === 'ERROR') {
    return (
      <P2GImageMessage
        image="/theme/smartsend/notifications/error.png"
        message="We're still getting things ready..."
        contentKey="image-myaccount-loading"
        showLoader
      />
    );
  }

  return <>{props.children}</>;
};

export default SettingsWrapper;
